import React, { useState } from 'react';
import { Input } from '../BossUI';
import PublicSurveyPage from './PublicSurveyPage';
import { Question, ScaleButtons, SymptomScaleGrid, HoursOfSleepChoice } from './surveyInputs';

const todayISODate = () => new Date().toISOString().slice(0, 10);

const openDatePicker = (e) => {
  if (typeof e.target.showPicker === 'function') {
    try { e.target.showPicker(); } catch (err) { /* unsupported in this browser */ }
  }
};

const REQUIRED_FIELDS = [
  'todays_date', 'musical_competency_rating',
  'music_for_connection_likelihood', 'music_for_coping_likelihood',
  'ptsd', 'tbi', 'depression', 'anxiety',
  'coping_days_needed', 'coping_days_comfortable', 'coping_strategy_use_likelihood',
  'good_nights_sleep_per_week', 'hours_of_sleep', 'music_as_sleep_aid_per_week',
];

const InitialSurveyFormFields = ({ submit }) => {
  const [values, setValues] = useState({ todays_date: todayISODate() });
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (field, val) => setValues((prev) => ({ ...prev, [field]: val }));
  const setSymptom = (field, val) => set(field, val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingFields = REQUIRED_FIELDS.filter(
      (f) => values[f] === undefined || values[f] === null || values[f] === ''
    );
    if (missingFields.length) {
      setMissing(missingFields);
      return;
    }
    setMissing([]);
    setSubmitting(true);
    try {
      await submit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-xl font-bold text-neutral-900 mb-1">Warrior Music Foundation Initial Survey</h2>

      {missing.length > 0 && (
        <p className="mt-3 text-sm text-red-600">Please answer all required questions before submitting.</p>
      )}

      <Question label="Today's Date" error={missing.includes('todays_date') ? 'Required' : null}>
        <Input
          type="date"
          value={values.todays_date || ''}
          onChange={(e) => set('todays_date', e.target.value)}
          onClick={openDatePicker}
          className="cursor-pointer"
        />
      </Question>

      <Question
        label="Please rate your (or your child's) musical competency from your point of view."
        error={missing.includes('musical_competency_rating') ? 'Required' : null}
      >
        <ScaleButtons
          min={0} max={10}
          value={values.musical_competency_rating}
          onChange={(v) => set('musical_competency_rating', v)}
          leftLabel="Unable to play" rightLabel="Proficient"
        />
      </Question>

      <Question
        label="How likely are you (or your child) to use music as a way to meet new people and connect?"
        error={missing.includes('music_for_connection_likelihood') ? 'Required' : null}
      >
        <ScaleButtons
          min={0} max={10}
          value={values.music_for_connection_likelihood}
          onChange={(v) => set('music_for_connection_likelihood', v)}
          leftLabel="Not at all likely" rightLabel="Extremely likely"
        />
      </Question>

      <Question
        label="How likely are you (or your child) to use music as a coping skill in everyday life?"
        error={missing.includes('music_for_coping_likelihood') ? 'Required' : null}
      >
        <ScaleButtons
          min={0} max={10}
          value={values.music_for_coping_likelihood}
          onChange={(v) => set('music_for_coping_likelihood', v)}
          leftLabel="Not at all likely" rightLabel="Extremely likely"
        />
      </Question>

      <Question
        label="Please rate your ability right now to cope with the symptoms of:"
        error={['ptsd', 'tbi', 'depression', 'anxiety'].some((f) => missing.includes(f)) ? 'Please rate all four' : null}
      >
        <SymptomScaleGrid values={values} onChange={setSymptom} />
      </Question>

      <Question
        label="Approximately how many days a week do you need to employ a coping strategy?"
        error={missing.includes('coping_days_needed') ? 'Required' : null}
      >
        <ScaleButtons min={1} max={7} value={values.coping_days_needed} onChange={(v) => set('coping_days_needed', v)} />
      </Question>

      <Question
        label="Approximately how many days a week do you feel comfortable actually using a coping strategy?"
        error={missing.includes('coping_days_comfortable') ? 'Required' : null}
      >
        <ScaleButtons min={1} max={7} value={values.coping_days_comfortable} onChange={(v) => set('coping_days_comfortable', v)} />
      </Question>

      <Question
        label="How likely are you to actually use a coping strategy you've been taught or given? (A coping strategy includes, but is not limited to deep breathing, positive imagery, safety checks, etc.)"
        error={missing.includes('coping_strategy_use_likelihood') ? 'Required' : null}
      >
        <ScaleButtons
          min={0} max={10}
          value={values.coping_strategy_use_likelihood}
          onChange={(v) => set('coping_strategy_use_likelihood', v)}
          leftLabel="I never use them" rightLabel="I use them as a preventative measure"
        />
      </Question>

      <Question
        label={'On average, how many nights a week would you describe yourself as getting a "good night\'s sleep?"'}
        error={missing.includes('good_nights_sleep_per_week') ? 'Required' : null}
      >
        <ScaleButtons min={1} max={7} value={values.good_nights_sleep_per_week} onChange={(v) => set('good_nights_sleep_per_week', v)} />
      </Question>

      <Question
        label="On average, how many hours of restful sleep do you get a night?"
        error={missing.includes('hours_of_sleep') ? 'Required' : null}
      >
        <HoursOfSleepChoice value={values.hours_of_sleep} onChange={(v) => set('hours_of_sleep', v)} />
      </Question>

      <Question
        label="How many nights a week do you use music as a sleep aid or as a relaxation tool to aid in sleep?"
        error={missing.includes('music_as_sleep_aid_per_week') ? 'Required' : null}
      >
        <ScaleButtons min={1} max={7} value={values.music_as_sleep_aid_per_week} onChange={(v) => set('music_as_sleep_aid_per_week', v)} />
      </Question>

      <div className="pt-6">
        <button
          type="submit"
          disabled={submitting}
          style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
          className="px-6 py-3 rounded-md text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

const InitialSurveyForm = () => (
  <PublicSurveyPage
    surveyType="initial"
    title="Initial Survey"
    renderForm={({ submit }) => <InitialSurveyFormFields submit={submit} />}
  />
);

export default InitialSurveyForm;
